import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import { CheckCircle, Star, Send, Loader2, ArrowRight, Hash, ShieldCheck, Tag } from "lucide-react";
import { usePublicLang, LangToggle } from "@/hooks/usePublicLang";

const API = process.env.REACT_APP_BACKEND_URL;

const SurveyPage = () => {
  const [searchParams] = useSearchParams();
  const { lang, setLang, t } = usePublicLang();
  const [clientCode, setClientCode] = useState("");
  const [isValidating, setIsValidating] = useState(false);
  const [validatedClient, setValidatedClient] = useState(null);
  const [receivedCoupon, setReceivedCoupon] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    satisfaction_rating: 5, print_quality: "", delivery_met_expectations: null, would_buy_again: null, improvement_suggestions: ""
  });

  useEffect(() => {
    const codeParam = searchParams.get("code");
    if (codeParam) setClientCode(codeParam.toUpperCase());
  }, [searchParams]);

  const handleChange = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const qualityOptions = [t("quality_excellent"), t("quality_good"), t("quality_fair"), t("quality_poor")];

  const handleValidateCode = async (e) => {
    e.preventDefault();
    if (!clientCode.trim()) return;
    setIsValidating(true);
    try {
      const res = await axios.post(`${API}/api/public/validate-client-code`, { code: clientCode.trim().toUpperCase() });
      setValidatedClient(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code");
    } finally { setIsValidating(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.print_quality || formData.delivery_met_expectations === null || formData.would_buy_again === null) {
      toast.error(lang === "es" ? "Completa todas las preguntas" : "Please complete all questions");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/public/survey`, {
        ...formData, client_code: validatedClient.client_code, name: validatedClient.client_name, email: validatedClient.client_email
      });
      if (res.data.coupon) setReceivedCoupon(res.data.coupon);
      setSubmitted(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  const langToggle = <div className="fixed top-4 right-4 z-50"><LangToggle lang={lang} setLang={setLang} /></div>;

  if (submitted) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4">
        {langToggle}
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6 border border-emerald-200">
            <CheckCircle className="w-10 h-10 text-emerald-600" />
          </div>
          <h1 className="text-3xl font-bold text-stone-900 mb-4">{t("survey_thanks_title")}</h1>
          <p className="text-stone-600 mb-8 text-lg">{t("survey_thanks_sub")}</p>
          {receivedCoupon && (
            <div className="bg-white border border-emerald-200 shadow-sm rounded-2xl p-6 mb-8 text-left">
              <div className="flex items-center gap-2 mb-3"><Tag className="w-5 h-5 text-emerald-600" /><p className="text-emerald-700 font-semibold">{t("survey_coupon_title")}</p></div>
              <p className="text-stone-800 text-sm mb-2">{receivedCoupon.description}</p>
              <div className="bg-stone-100 rounded-xl p-4 text-center">
                <p className="text-3xl font-mono font-bold text-emerald-700 tracking-widest" data-testid="survey-coupon-code">{receivedCoupon.code}</p>
                <p className="text-stone-500 text-sm mt-1">{receivedCoupon.discount_type === 'percentage' ? `${receivedCoupon.discount_value}%` : `$${receivedCoupon.discount_value}`} {t("survey_discount")}</p>
              </div>
              <p className="text-stone-500 text-xs mt-3">{t("survey_coupon_present")}</p>
            </div>
          )}
          <Button onClick={() => window.location.href = "/"} className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white h-12 px-8 rounded-xl" data-testid="survey-back-home-btn">
            {t("survey_back_home")} <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </div>
    );
  }

  if (!validatedClient) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center p-4" data-testid="survey-code-screen">
        {langToggle}
        <div className="max-w-md w-full">
          <div className="text-center mb-10">
            <img src="/assets/logo-sally-dark.png" alt="Sally Better" className="h-16 mx-auto mb-6" onError={(e) => e.target.style.display = 'none'} />
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-5 border border-emerald-200"><ShieldCheck className="w-8 h-8 text-emerald-600" /></div>
            <h1 className="text-3xl font-bold text-stone-900 mb-3">{t("survey_title")}</h1>
            <p className="text-stone-600 text-lg">{t("survey_enter_code")}</p>
          </div>
          <form onSubmit={handleValidateCode} className="bg-white border border-stone-200 shadow-sm rounded-2xl p-8">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-stone-700 flex items-center gap-2"><Hash className="w-4 h-4" /> {t("survey_code_label")}</Label>
                <Input value={clientCode} onChange={(e) => setClientCode(e.target.value.toUpperCase().slice(0, 6))} placeholder={t("survey_code_placeholder")} maxLength={6}
                  className="bg-white border-stone-300 text-stone-900 placeholder:text-stone-400 focus:border-emerald-500 text-center text-2xl font-mono tracking-[0.3em] h-14" data-testid="survey-code-input" />
                <p className="text-xs text-stone-500 text-center">{t("survey_code_help")}</p>
              </div>
              <Button type="submit" disabled={isValidating || clientCode.length < 6}
                className="w-full h-12 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl font-semibold" data-testid="survey-validate-btn">
                {isValidating ? <Loader2 className="w-5 h-5 animate-spin" /> : t("survey_verify")}
              </Button>
            </div>
          </form>
          <p className="text-center text-sm text-stone-500 mt-6">{t("survey_no_code")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 py-16 px-4" data-testid="survey-page">
      {langToggle}
      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-10">
          <img src="/assets/logo-sally-dark.png" alt="Sally Better" className="h-16 mx-auto mb-6" onError={(e) => e.target.style.display = 'none'} />
          <p className="text-emerald-700 text-sm font-medium tracking-wider uppercase mb-3">{t("survey_subtitle")}</p>
          <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mb-2">{t("survey_title")}</h1>
          <p className="text-stone-600">{t("survey_hello")} <span className="text-emerald-700 font-medium">{validatedClient.client_name}</span>, {t("survey_tell_experience")}</p>
        </div>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6 md:p-8" data-testid="survey-q1">
            <h2 className="text-lg font-semibold text-stone-900 mb-5">{t("survey_q1")}</h2>
            <div className="flex items-center justify-between">
              <span className="text-sm text-stone-500 hidden sm:block">{t("survey_very_unsatisfied")}</span>
              <div className="flex gap-3 mx-auto sm:mx-0">
                {[1,2,3,4,5].map(r => (
                  <button key={r} type="button" onClick={() => handleChange("satisfaction_rating", r)}
                    className={`w-12 h-12 rounded-full flex items-center justify-center transition-all duration-200 ${formData.satisfaction_rating >= r ? "bg-amber-400 text-white scale-110" : "bg-stone-200 text-stone-400 hover:bg-stone-300"}`}
                    data-testid={`survey-star-${r}`}><Star className={`w-6 h-6 ${formData.satisfaction_rating >= r ? "fill-current" : ""}`} /></button>
                ))}
              </div>
              <span className="text-sm text-stone-500 hidden sm:block">{t("survey_very_satisfied")}</span>
            </div>
            <p className="text-center mt-3 text-sm font-medium text-emerald-700">{formData.satisfaction_rating} {t("survey_of")} 5</p>
          </div>
          <div className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6 md:p-8" data-testid="survey-q2">
            <h2 className="text-lg font-semibold text-stone-900 mb-5">{t("survey_q2")}</h2>
            <RadioGroup value={formData.print_quality} onValueChange={(v) => handleChange("print_quality", v)} className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {qualityOptions.map(opt => (
                <div key={opt} className="flex items-center"><RadioGroupItem value={opt} id={`q-${opt}`} className="peer sr-only" />
                  <Label htmlFor={`q-${opt}`} className="flex-1 cursor-pointer rounded-xl border-2 border-stone-300 p-3 text-center text-stone-700 hover:border-emerald-400 peer-data-[state=checked]:border-emerald-600 peer-data-[state=checked]:bg-emerald-50 peer-data-[state=checked]:text-emerald-700 transition-all">{opt}</Label>
                </div>
              ))}
            </RadioGroup>
          </div>
          <div className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6 md:p-8" data-testid="survey-q3">
            <h2 className="text-lg font-semibold text-stone-900 mb-5">{t("survey_q3")}</h2>
            <RadioGroup value={formData.delivery_met_expectations === null ? "" : formData.delivery_met_expectations.toString()} onValueChange={(v) => handleChange("delivery_met_expectations", v === "true")} className="grid grid-cols-2 gap-4">
              <div className="flex items-center"><RadioGroupItem value="true" id="del-y" className="peer sr-only" />
                <Label htmlFor="del-y" className="flex-1 cursor-pointer rounded-xl border-2 border-stone-300 p-5 text-center text-stone-700 hover:border-emerald-400 peer-data-[state=checked]:border-emerald-600 peer-data-[state=checked]:bg-emerald-50 transition-all" data-testid="survey-delivery-yes">
                  <CheckCircle className="w-7 h-7 mx-auto mb-2 text-emerald-600" /><span className="text-lg font-medium">{t("survey_yes")}</span></Label></div>
              <div className="flex items-center"><RadioGroupItem value="false" id="del-n" className="peer sr-only" />
                <Label htmlFor="del-n" className="flex-1 cursor-pointer rounded-xl border-2 border-stone-300 p-5 text-center text-stone-700 hover:border-red-400 peer-data-[state=checked]:border-red-500 peer-data-[state=checked]:bg-red-50 transition-all" data-testid="survey-delivery-no">
                  <span className="block w-7 h-7 mx-auto mb-2 text-red-500 text-2xl leading-7">&#10005;</span><span className="text-lg font-medium">{t("survey_no")}</span></Label></div>
            </RadioGroup>
          </div>
          <div className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6 md:p-8" data-testid="survey-q4">
            <h2 className="text-lg font-semibold text-stone-900 mb-5">{t("survey_q4")}</h2>
            <RadioGroup value={formData.would_buy_again === null ? "" : formData.would_buy_again.toString()} onValueChange={(v) => handleChange("would_buy_again", v === "true")} className="grid grid-cols-2 gap-4">
              <div className="flex items-center"><RadioGroupItem value="true" id="buy-y" className="peer sr-only" />
                <Label htmlFor="buy-y" className="flex-1 cursor-pointer rounded-xl border-2 border-stone-300 p-5 text-center text-stone-700 hover:border-emerald-400 peer-data-[state=checked]:border-emerald-600 peer-data-[state=checked]:bg-emerald-50 transition-all" data-testid="survey-buyagain-yes">
                  <CheckCircle className="w-7 h-7 mx-auto mb-2 text-emerald-600" /><span className="text-lg font-medium">{t("survey_yes")}</span></Label></div>
              <div className="flex items-center"><RadioGroupItem value="false" id="buy-n" className="peer sr-only" />
                <Label htmlFor="buy-n" className="flex-1 cursor-pointer rounded-xl border-2 border-stone-300 p-5 text-center text-stone-700 hover:border-red-400 peer-data-[state=checked]:border-red-500 peer-data-[state=checked]:bg-red-50 transition-all" data-testid="survey-buyagain-no">
                  <span className="block w-7 h-7 mx-auto mb-2 text-red-500 text-2xl leading-7">&#10005;</span><span className="text-lg font-medium">{t("survey_no")}</span></Label></div>
            </RadioGroup>
          </div>
          <div className="bg-white border border-stone-200 shadow-sm rounded-2xl p-6 md:p-8" data-testid="survey-q5">
            <h2 className="text-lg font-semibold text-stone-900 mb-1">{t("survey_q5")}</h2>
            <p className="text-sm text-stone-500 mb-4">{t("survey_q5_sub")}</p>
            <Textarea value={formData.improvement_suggestions} onChange={(e) => handleChange("improvement_suggestions", e.target.value)}
              placeholder={t("survey_suggestions_placeholder")} rows={4}
              className="bg-white border-stone-300 text-stone-900 placeholder:text-stone-400 focus:border-emerald-500" data-testid="survey-suggestions-textarea" />
          </div>
          <Button type="submit" disabled={loading} className="w-full h-14 text-lg bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white rounded-xl font-semibold" data-testid="survey-submit-btn">
            {loading ? (<><Loader2 className="w-5 h-5 mr-2 animate-spin" />{t("survey_submitting")}</>) : (<><Send className="w-5 h-5 mr-2" />{t("survey_submit")}</>)}
          </Button>
          <p className="text-center text-sm text-stone-500 mt-4 pb-8">{t("survey_consent")}</p>
        </form>
      </div>
    </div>
  );
};

export default SurveyPage;
